import { useState } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';

// Public set-password page reached from an admin-issued activation link (/activate?token=...).
// Recreated (reduced) from the uniticket ResetPassword page — no 2FA branch.
export function Activate() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { t } = useTranslation('common');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(t('activate.mismatch'));
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/auth/activate', { method: 'POST', body: JSON.stringify({ token, password }) });
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('activate.genericError'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-center justify-center px-6 py-12">
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6 relative z-10">
        <div className="text-center">
          <h2 className="text-2xl font-bold gradient-text">Address Validation</h2>
        </div>

        {done ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto" />
            <div>
              <h2 className="text-lg font-bold text-white">{t('activate.successTitle')}</h2>
              <p className="text-white/60 mt-1 text-sm">{t('activate.successDesc')}</p>
            </div>
            <Link
              to="/login"
              className="inline-block w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300"
            >
              {t('activate.signIn')}
            </Link>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-xl font-bold text-white">{t('activate.title')}</h2>
              <p className="text-white/60 mt-1 text-sm">{t('activate.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1.5">
                  {t('activate.passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-[11px] text-white/40 mt-1">{t('activate.hint')}</p>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-white/80 mb-1.5">
                  {t('activate.confirmLabel')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={loading}
                  className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {loading ? t('activate.submitting') : t('activate.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
